import React, { useState, useEffect } from "react";
import ScrollContainer from "react-indiana-drag-scroll";
import styled from "styled-components";
import axios from "axios";
import { useSelector } from "react-redux";

import { Container } from "../layout/Container";
import LiveSlider from "../components/carousel/LiveSlider";
import GameCard from "../components/cards/Game";
import ReplayCard from "../components/cards/Replay";
// import TournamentCard from "../components/cards/Tournament";
import { ENV_CONFIG } from "../config/environment-unified";
import useTranslation from "../hooks/useTranslation";
import { getPublicReplays } from "../services/replaysApi";

// Composant de debug temporaire
/* const DebugCard = ({ title, data, type }) => (
  <div style={{
    border: '2px solid #ff4444',
    padding: '10px',
    margin: '10px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    minWidth: '200px'
  }}>
    <h4 style={{ color: '#ff4444', margin: '0 0 10px 0' }}>{title}</h4>
    <p><strong>Type:</strong> {type}</p>
    <p><strong>Nombre:</strong> {data?.length || 0}</p>
    {data && data.length > 0 && (
      <div>
        <p><strong>Premier élément:</strong></p>
        <pre style={{ fontSize: '10px', overflow: 'auto' }}>
          {JSON.stringify(data[0], null, 2)}
        </pre>
      </div>
    )}
  </div>
); */

export const Home = () => {
  // console.log("🏠 [HOME] Composant Home monté");
  // console.log("🔧 [HOME] ENV_CONFIG:", ENV_CONFIG);

  return (
    <Container main>
      <LiveSlider style={{ marginBottom: "70px" }} />
      <LiveTournaments />
      <Games />
      <Replays />
      {/* <CardsContainer
        style={{ marginTop: "70px" }}
        cardsselection={VideoCards}
        title="Recommended Games"
        // tomore="/videos"
      /> */}
      {/* <Clipped /> */}
      {/* <SuggestedVideos /> */}
    </Container>
  );
};
export default Home;

const LiveTournaments = () => {
  const { t } = useTranslation();
  const userId = useSelector((state) => state.user?.data?.id);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);

        // Get ALL live streams so small tournaments still appear (not only top 6)
        const streamsRes = await axios.get(ENV_CONFIG.STREAMS_ENDPOINT, { withCredentials: true });
        const streams = streamsRes.data || [];

        // Group by currentEventId
        const byEventId = new Map();
        for (const s of streams) {
          if (!s?.currentEventId) continue;
          const key = String(s.currentEventId);
          const prev = byEventId.get(key) || { eventId: key, viewerCount: 0, povCount: 0, streams: [] };
          prev.viewerCount += Number(s.viewerCount || 0);
          prev.povCount += 1;
          prev.streams.push(s);
          byEventId.set(key, prev);
        }

        const eventIds = Array.from(byEventId.keys()).slice(0, 30);
        if (eventIds.length === 0) {
          if (mounted) setEvents([]);
          return;
        }

        const fetched = await Promise.all(
          eventIds.map(async (eventId) => {
            try {
              const evRes = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/by-id/${eventId}`, { withCredentials: true });
              const ev = evRes.data?.event;
              if (!ev) return null;
              return { ...ev, _liveMeta: byEventId.get(String(eventId)) };
            } catch {
              return null;
            }
          })
        );

        const liveTournaments = fetched
          .filter(Boolean)
          .filter((ev) => ev?.category?.slug === "tournament")
          .sort((a, b) => (b._liveMeta?.viewerCount || 0) - (a._liveMeta?.viewerCount || 0));

        if (mounted) setEvents(liveTournaments);
      } catch (e) {
        if (mounted) setError(e);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Carousel>
        <header style={{ marginBottom: "32px" }}>
          <h2>{t("home.live_tournaments", "Live Tournaments")}</h2>
        </header>
        <div style={{ padding: "20px", color: "#666" }}>{t("home.loading_live_tournaments", "Loading live tournaments...")}</div>
      </Carousel>
    );
  }

  if (error) {
    return (
      <Carousel>
        <header style={{ marginBottom: "32px" }}>
          <h2>{t("home.live_tournaments", "Live Tournaments")}</h2>
        </header>
        <div style={{ padding: "20px", color: "#ff4444" }}>
          {t("home.live_tournaments_error", "Failed to load live tournaments")}
        </div>
      </Carousel>
    );
  }

  if (!events || events.length === 0) {
    return null; // keep home clean if none
  }

  return (
    <Carousel>
      <header style={{ marginBottom: "32px" }}>
        <h2>{t("home.live_tournaments", "Live Tournaments")} ({events.length})</h2>
      </header>
      <ScrollContainer vertical={false} style={{ display: "flex", gap: "10px" }}>
        {events.slice(0, 12).map((ev) => {
          const isOwner = userId && String(ev.createdBy) === String(userId);
          const href = isOwner ? `/events/${ev.slug}/dashboard` : `/events/${ev.slug}`;
          return (
            <TournamentEventCard key={ev.id} href={href}>
              {ev.coverImage ? (
                <TournamentCover
                  src={ev.coverImage}
                  alt={ev.name}
                  onError={(e) => {
                    // Avoid infinite error loops + avoid fetching a fallback image repeatedly
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <TournamentCoverFallback>
                  <span>🏆</span>
                  <small>{ev.name}</small>
                </TournamentCoverFallback>
              )}
              <TournamentBody>
                <TournamentTitleRow>
                  <span>{ev.name}</span>
                  <LiveBadge>LIVE</LiveBadge>
                </TournamentTitleRow>
                <TournamentMeta>
                  <span>{ev._liveMeta?.povCount || 1} POV</span>
                  <span>•</span>
                  <span>{ev._liveMeta?.viewerCount || 0} viewers</span>
                </TournamentMeta>
                <TournamentHint>
                  {isOwner
                    ? t("home.open_dashboard", "Open dashboard")
                    : t("home.open_event", "Open event")}
                </TournamentHint>
              </TournamentBody>
            </TournamentEventCard>
          );
        })}
      </ScrollContainer>
    </Carousel>
  );
};

const TournamentEventCard = styled.a`
  display: block;
  min-width: 280px;
  max-width: 280px;
  text-decoration: none;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.03);
  transition: transform 0.15s ease, border-color 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    border-color: rgba(255,255,255,0.16);
  }
`;

const TournamentCover = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
  background: #111;
`;

const TournamentCoverFallback = styled.div`
  width: 100%;
  height: 160px;
  background: #111827;
  color: rgba(255,255,255,0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  text-align: center;
  small {
    opacity: 0.75;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const TournamentBody = styled.div`
  padding: 12px;
`;

const TournamentTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.textColor || "#fff"};
  font-weight: 700;
  font-size: 0.95rem;
`;

const LiveBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 800;
  color: white;
  background: #ef4444;
  padding: 4px 8px;
  border-radius: 999px;
`;

const TournamentMeta = styled.div`
  margin-top: 6px;
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${({ theme }) => theme.textSecondary || "#999"};
  font-size: 0.85rem;
`;

const TournamentHint = styled.div`
  margin-top: 10px;
  color: ${({ theme }) => theme.color?.primary || "#4f8cff"};
  font-size: 0.85rem;
  font-weight: 700;
`;

/* 
const RecommendedTournaments = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // console.log("🏆 [TOURNAMENTS] Début du fetch des tournois");
    // console.log("🔗 [TOURNAMENTS] URL:", ENV_CONFIG.TOURNAMENT_GET_TOURNAMENTS_ENDPOINT);

    setLoading(true);
    setError(null);

    axios
      .get(ENV_CONFIG.TOURNAMENT_GET_TOURNAMENTS_ENDPOINT, { withCredentials: true })
      .then((res) => {
        // console.log("✅ [TOURNAMENTS] Succès:", res.data);
        // console.log("📊 [TOURNAMENTS] Nombre de tournois:", res.data.length);
        // console.log("📊 [TOURNAMENTS] Données brutes:", JSON.stringify(res.data, null, 2));
        setData(res.data);
        setLoading(false);
      })
      .catch((error) => {
        // console.error("❌ [TOURNAMENTS] Erreur:", error);
        // console.error("❌ [TOURNAMENTS] Status:", error.response?.status);
        // console.error("❌ [TOURNAMENTS] Data:", error.response?.data);
        setError(error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Carousel>
        <header>
          <h2>{t('home.recommended_tournaments')}</h2>
        </header>
        <div style={{ padding: "20px", color: "#666" }}>{t('home.chargement_des_tournois')}</div>
      </Carousel>
    );
  }

  if (error) {
    return (
      <Carousel>
        <header>
          <h2>{t('home.recommended_tournaments')}</h2>
        </header>
        <div style={{ padding: "20px", color: "#ff4444" }}>
          {error.response?.data?.msg || error.message}
        </div>
      </Carousel>
    );
  }

  if (data.length === 0) {
    return (
      <Carousel>
        <header>
          <h2>{t('home.recommended_tournaments')}</h2>
        </header>
        <div style={{ padding: "20px", color: "#666" }}>{t('home.aucun_tournoi_disponible')}</div>
      </Carousel>
    );
  }

  var rows = [];
  for (let i = 0; i < Math.min(6, data.length); i++) {
    const tournament = data[i];
    // console.log(`🏆 [TOURNAMENTS] Rendu tournoi ${i}:`, tournament);

    rows.push(
      <TournamentCard
        thumbnail={tournament?.thumbnail}
        avatar={tournament?.adminAvatar}
        username={tournament?.username}
        title={tournament?.title}
        id={tournament?.id}
        state="available"
        // tags={tournament?.tags}
        key={i}
        style={{ marginRight: "10px" }}
      />
    );
  }

  return (
    <Carousel>
      <header>
        <h2>{t('home.recommended_tournaments')} ({data.length})</h2>
      </header>
      <ScrollContainer vertical={false}>{rows}</ScrollContainer>
    </Carousel>
  );
}; */

const Games = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // console.log("🎮 [GAMES] Début du fetch des jeux");
    // console.log("🔗 [GAMES] URL:", ENV_CONFIG.GAMES_ENDPOINT);

    setLoading(true);
    setError(null);

    axios
      .get(ENV_CONFIG.GAMES_ENDPOINT, { withCredentials: true })
      .then((res) => {
        // console.log("✅ [GAMES] Succès:", res.data);
        // console.log("📊 [GAMES] Nombre de jeux:", res.data.length);
        // console.log("📊 [GAMES] Données brutes:", JSON.stringify(res.data, null, 2));
        setData(res.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("❌ [GAMES] Erreur:", error);
        console.error("❌ [GAMES] Status:", error.response?.status);
        console.error("❌ [GAMES] Data:", error.response?.data);
        setError(error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Carousel>
        <header style={{ marginBottom: "32px" }}>
          <h2>Trending Games</h2>
        </header>
        <div style={{ padding: "20px", color: "#666" }}>Chargement des jeux...</div>
      </Carousel>
    );
  }

  if (error) {
    return (
      <Carousel>
        <header style={{ marginBottom: "32px" }}>
          <h2>Trending Games</h2>
        </header>
        <div style={{ padding: "20px", color: "#ff4444" }}>
          Erreur: {error.response?.data?.msg || error.message}
        </div>
      </Carousel>
    );
  }

  if (data.length === 0) {
    return (
      <Carousel>
        <header style={{ marginBottom: "32px" }}>
          <h2>Trending Games</h2>
        </header>
        <div style={{ padding: "20px", color: "#666" }}>Aucun jeu disponible</div>
        {/* <DebugCard title="Debug Games" data={data} type="game" /> */}
      </Carousel>
    );
  }

  var rows = [];
  for (let i = 0; i < data.length; i++) {
    const g = data[i] || {};
    const img = g.landscapeImage || g.portraitImage || g.icon;
    const name = g.name || g.shortName || "Unknown Game";
    const gameId = g.id || g._id || g.slug || g.name;
    rows.push(
      <GameCard
        to={gameId}
        name={name}
        thumbnail={img}
        key={i}
        style={{ marginRight: "10px" }}
      />
    );
  }

  return (
    <Carousel>
      <header style={{ marginBottom: "32px" }}>
        <h2>{t('home.recommended_games')} ({data.length})</h2>
      </header>
      <ScrollContainer vertical={false} style={{ display: "flex", gap: "10px" }}>
        {rows}
      </ScrollContainer>
      {/* <DebugCard title="Debug Games" data={data} type="game" /> */}
    </Carousel>
  );
};

const Replays = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    getPublicReplays(1, 6)
      .then((response) => {
        setData(response.replays || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("❌ [REPLAYS] Error:", error);
        setError(error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <ReplaysCarousel>
        <header style={{ marginBottom: "32px" }}>
          <h2>{t('home.recent_replays') || 'Recent Replays'}</h2>
        </header>
        <div style={{ padding: "20px", color: "#666" }}>Loading replays...</div>
      </ReplaysCarousel>
    );
  }

  if (error) {
    return (
      <ReplaysCarousel>
        <header style={{ marginBottom: "32px" }}>
          <h2>{t('home.recent_replays') || 'Recent Replays'}</h2>
        </header>
        <div style={{ padding: "20px", color: "#ff4444" }}>
          Error loading replays
        </div>
      </ReplaysCarousel>
    );
  }

  if (data.length === 0) {
    return (
      <ReplaysCarousel>
        <header style={{ marginBottom: "32px" }}>
          <h2>{t('home.recent_replays') || 'Recent Replays'}</h2>
        </header>
        <div style={{ padding: "20px", color: "#666" }}>No replays available</div>
      </ReplaysCarousel>
    );
  }

  var rows = [];
  for (let i = 0; i < Math.min(6, data.length); i++) {
    const replay = data[i];
    rows.push(
      <ReplayCard
        key={replay.id}
        replay={replay}
        style={{ marginRight: "10px" }}
      />
    );
  }

  return (
    <ReplaysCarousel>
      <header style={{ marginBottom: "32px" }}>
        <h2>{t('home.recent_replays') || 'Recent Replays'} ({data.length})</h2>
      </header>
      <ScrollContainer vertical={false} style={{ display: "flex", gap: "10px" }}>
        {rows}
      </ScrollContainer>
    </ReplaysCarousel>
  );
};

const Carousel = styled.section`
  position: relative;
  margin-bottom: 50px;
  height: 300px;

  > div {
    position: absolute;
    left: 0;
    top: 60px;
    width: 80vw;
    max-width: 1940px;
    display: flex;
  }
  @media (max-width: 576px) {
    margin-bottom: 30px;
  }
`;

const ReplaysCarousel = styled.section`
  position: relative;
  margin-bottom: 50px;
  min-height: 450px;

  > div {
    position: absolute;
    left: 0;
    top: 60px;
    width: 80vw;
    max-width: 1940px;
    display: flex;
    gap: 10px;
  }
  @media (max-width: 576px) {
    margin-bottom: 30px;
    min-height: 350px;
  }
`;
